import type { CSSProperties } from 'react';

const defaultTheme = {
  name: 'default',
  sizing: {
    height: '40px',
    dropdownGap: '5px',
    homeTabWidth: '36px',
  },
  colors: {
    common: {
      dividerColor: 'rgba(95, 94, 101, 0.5)',
    },
    inactive: {
      bg: '#29292F',
      hoverBg: '#37363F',
      color: '#ffffff',
      closeBg: 'rgba(0, 0, 0, 0)',
      hoverCloseBg: 'rgba(249, 249, 249, 0.3)',
    },
    active: {
      bg: '#37363F',
      hoverBg: '#37363F',
      color: '#ffffff',
      closeBg: 'rgba(0, 0, 0, 0)',
      hoverCloseBg: 'rgba(249, 249, 249, 0.3)',
    },
  },
};

type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

export type DefaultThemeType = typeof defaultTheme;

type CreateThemeTypeDeepPartial = DeepPartial<typeof defaultTheme>;

// Make name required
export interface CreateThemeType
  extends Omit<CreateThemeTypeDeepPartial, 'name'> {
  name: string;
}

export const createTheme = (
  theme: CreateThemeType,
  themeToSpread: DefaultThemeType = defaultTheme
) => ({
  name: theme.name,
  sizing: {
    ...themeToSpread.sizing,
    ...theme.sizing,
  },
  colors: {
    common: {
      ...themeToSpread.colors.common,
      ...theme.colors?.common,
    },
    inactive: {
      ...themeToSpread.colors.inactive,
      ...theme.colors?.inactive,
    },
    active: {
      ...themeToSpread.colors.active,
      ...theme.colors?.active,
    },
  },
});

export const baseTheme = createTheme({
  name: 'base',
});

/**
 * To create a custom theme do the following:
 * 1. Create a new theme object
 * 2. Use the createTheme function to create a new theme.
 *      Note: The createTheme function will merge the new theme with the base theme.
 * 3. Export the new theme
 * 4. use setTheme from useUserSettings to set the new theme (inside useEffect
 *    or on some action/event) and pass the created theme as an argument.
 *
 * Example:
 * export const myCustomTheme = createTheme({
 *   name: 'myCustomTheme',
 *   colors: {
 *     inactive: {
 *       bg: '#29292F',
 *       hoverBg: '#37363F',
 *       color: '#ffffff',
 *       hoverCloseBg: 'rgba(249, 249, 249, 0.3)',
 *     },
 *     active: {
 *       bg: '#37363F',
 *       hoverBg: '#37363F',
 *       color: '#ffffff',
 *       hoverCloseBg: 'rgba(249, 249, 249, 0.3)',
 *     },
 *   },
 * });
 *
 * */

export const getCSSProperties = (theme: DefaultThemeType) => {
  return {
    '--ds-tab-sizing-height': theme.sizing.height,
    '--ds-tab-sizing-dropdown-gap': theme.sizing.dropdownGap,
    '--ds-tab-sizing-home-tab-width': theme.sizing.homeTabWidth,

    '--ds-tab-common-divider-color': theme.colors.common.dividerColor,

    '--ds-tab-inactive-bg': theme.colors.inactive.bg,
    '--ds-tab-inactive-hover-bg': theme.colors.inactive.hoverBg,
    '--ds-tab-inactive-color': theme.colors.inactive.color,
    '--ds-tab-inactive-close-bg': theme.colors.inactive.closeBg,
    '--ds-tab-inactive-hover-close-bg': theme.colors.inactive.hoverCloseBg,
    '--ds-tab-active-bg': theme.colors.active.bg,
    '--ds-tab-active-hover-bg': theme.colors.active.hoverBg,
    '--ds-tab-active-color': theme.colors.active.color,
    '--ds-tab-active-close-bg': theme.colors.active.closeBg,
    '--ds-tab-active-hover-close-bg': theme.colors.active.hoverCloseBg,
  } as CSSProperties;
};
